'use client'

import React, { createContext, useCallback, useContext, useEffect, useState } from 'react'

interface ToastProps {
  title: string
  description?: string
  variant?: 'default' | 'destructive' | 'success'
}

interface Toast extends ToastProps {
  id: string
  createdAt: number
}

interface ToastContextType {
  toast: (props: ToastProps) => void
  toasts: Toast[]
  dismiss: (id: string) => void
}

const ToastContext = createContext<ToastContextType | undefined>(undefined)

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])

  const toast = useCallback(({ title, description, variant = 'default' }: ToastProps) => {
    const newToast: Toast = {
      id: Math.random().toString(36).substr(2, 9),
      title,
      description,
      variant,
      createdAt: Date.now()
    }

    setToasts(prev => [...prev, newToast])

    // Auto-remove toast after 5 seconds
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== newToast.id))
    }, 5000)
  }, [])

  const dismiss = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id))
  }, [])

  // Clean up old toasts
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now()
      setToasts(prev => prev.filter(t => now - t.createdAt < 5000))
    }, 1000)

    return () => clearInterval(interval)
  }, [])

  return (
    <ToastContext.Provider value={{ toast, toasts, dismiss }}>
      {children}
    </ToastContext.Provider>
  )
}

export function useToast() {
  const context = useContext(ToastContext)
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider')
  }
  return context
}
